import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, LogOut, KeyRound, CalendarX } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(
    () => localStorage.getItem('admin_must_change_password') === '1'
  );

  // Block dates form state
  const [blockForm, setBlockForm] = useState({
    room_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [creatingBlock, setCreatingBlock] = useState(false);

  const [roomForm, setRoomForm] = useState({
    name_it: '',
    name_en: '',
    description_it: '',
    description_en: '',
    price: '',
    max_guests: '',
    image_url: '',
    amenities_it: [],
    amenities_en: []
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    // Attach Bearer token to all axios requests while dashboard is mounted
    const reqId = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem('admin_token');
      if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    // Handle 401 -> redirect to login
    const resId = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin');
        }
        return Promise.reject(err);
      }
    );
    fetchData();
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    if (localStorage.getItem('admin_must_change_password') === '1') {
      setLoading(false);
      return;
    }
    try {
      const [bookingsRes, roomsRes, blocksRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/rooms`),
        axios.get(`${API}/admin/blocked-dates`)
      ]);
      setBookings(bookingsRes.data);
      setRooms(roomsRes.data);
      setBlockedDates(blocksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleSaveRoom = async (e) => {
    e.preventDefault();
    
    try {
      const roomData = {
        ...roomForm,
        price: parseFloat(roomForm.price),
        max_guests: parseInt(roomForm.max_guests)
      };

      if (editingRoom) {
        await axios.put(`${API}/rooms/${editingRoom.room_id}`, roomData);
        toast.success(language === 'it' ? 'Camera aggiornata' : 'Room updated');
      } else {
        await axios.post(`${API}/rooms`, roomData);
        toast.success(language === 'it' ? 'Camera creata' : 'Room created');
      }

      setIsRoomDialogOpen(false);
      setEditingRoom(null);
      resetRoomForm();
      fetchData();
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error(t('common.error'));
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm(language === 'it' ? 'Confermi eliminazione?' : 'Confirm deletion?')) {
      return;
    }

    try {
      await axios.delete(`${API}/rooms/${roomId}`);
      toast.success(language === 'it' ? 'Camera eliminata' : 'Room deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error(t('common.error'));
    }
  };

  const openEditDialog = (room) => {
    setEditingRoom(room);
    setRoomForm({
      name_it: room.name_it,
      name_en: room.name_en,
      description_it: room.description_it,
      description_en: room.description_en,
      price: room.price.toString(),
      max_guests: room.max_guests.toString(),
      image_url: room.image_url,
      amenities_it: room.amenities_it || [],
      amenities_en: room.amenities_en || []
    });
    setIsRoomDialogOpen(true);
  };

  const resetRoomForm = () => {
    setRoomForm({
      name_it: '',
      name_en: '',
      description_it: '',
      description_en: '',
      price: '',
      max_guests: '',
      image_url: '',
      amenities_it: [],
      amenities_en: []
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    const strongEnough =
      newPassword.length >= 10 &&
      /[A-Z]/.test(newPassword) &&
      /[a-z]/.test(newPassword) &&
      /[0-9]/.test(newPassword);
    if (!strongEnough) {
      toast.error(language === 'it'
        ? 'La password deve avere almeno 10 caratteri, maiuscola, minuscola e numero'
        : 'Password must be at least 10 characters with upper, lower and number');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(language === 'it' ? 'Le password non coincidono' : 'Passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success(language === 'it' ? 'Password aggiornata' : 'Password updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      // Clear must-change flag; user needs to log in again with new password
      if (localStorage.getItem('admin_must_change_password') === '1') {
        localStorage.removeItem('admin_must_change_password');
        localStorage.removeItem('admin_token');
        setMustChangePassword(false);
        toast.success(language === 'it' ? 'Effettua nuovamente il login' : 'Please log in again');
        navigate('/admin');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || t('common.error');
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    
    if (!blockForm.room_id || !blockForm.start_date || !blockForm.end_date) {
      toast.error(language === 'it' ? 'Compila tutti i campi obbligatori' : 'Please fill required fields');
      return;
    }
    
    if (blockForm.start_date >= blockForm.end_date) {
      toast.error(language === 'it' ? 'Data inizio deve essere prima della fine' : 'Start date must be before end date');
      return;
    }

    setCreatingBlock(true);
    try {
      await axios.post(`${API}/admin/blocked-dates`, blockForm);
      toast.success(language === 'it' ? 'Date bloccate' : 'Dates blocked');
      setBlockForm({ room_id: '', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || t('common.error');
      toast.error(msg);
    } finally {
      setCreatingBlock(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm(language === 'it' ? 'Rimuovere questo blocco?' : 'Remove this block?')) return;
    
    try {
      await axios.delete(`${API}/admin/blocked-dates/${blockId}`);
      toast.success(language === 'it' ? 'Blocco rimosso' : 'Block removed');
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#8A9A86] text-xl">{t('common.loading')}</p>
      </div>
    );
  }

  if (mustChangePassword) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-10 border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="force-change-password-card">
          <h1
            className="text-3xl font-light text-[#2C302E] mb-3 text-center tracking-tight"
            style={{fontFamily: 'Cormorant Garamond, serif'}}
          >
            {language === 'it' ? 'Cambia Password' : 'Change Password'}
          </h1>
          <p className="text-sm text-[#6B7568] mb-6 text-center">
            {language === 'it'
              ? 'Per motivi di sicurezza devi cambiare la password predefinita prima di accedere al pannello.'
              : 'For security reasons you must change the default password before accessing the panel.'}
          </p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label htmlFor="current-pw">{language === 'it' ? 'Password attuale' : 'Current password'}</Label>
              <Input id="current-pw" type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="current-password-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="new-pw">{language === 'it' ? 'Nuova password' : 'New password'}</Label>
              <Input id="new-pw" type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="new-password-input" className="mt-1" />
              <p className="text-xs text-[#8A9A86] mt-1">
                {language === 'it' ? 'Min 10 caratteri, maiuscola, minuscola e numero' : 'Min 10 chars, upper, lower, number'}
              </p>
            </div>
            <div>
              <Label htmlFor="confirm-pw">{language === 'it' ? 'Conferma nuova password' : 'Confirm new password'}</Label>
              <Input id="confirm-pw" type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="confirm-password-input" className="mt-1" />
            </div>
            <Button type="submit" disabled={changingPassword} data-testid="submit-force-change-password" className="w-full bg-[#8A9A86] hover:bg-[#7A8A76] text-white rounded-full py-6">
              {changingPassword ? t('common.loading') : (language === 'it' ? 'Aggiorna password' : 'Update password')}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-4xl font-light text-[#2C302E] tracking-tight"
            style={{fontFamily: 'Cormorant Garamond, serif'}}
            data-testid="admin-dashboard-title"
          >
            {t('admin.dashboard')}
          </h1>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="border-[#8A9A86] text-[#8A9A86] hover:bg-[#8A9A86] hover:text-white"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('admin.logout')}
          </Button>
        </div>

        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-[#F0F2EB]">
            <TabsTrigger value="bookings" data-testid="bookings-tab">{t('admin.bookings')}</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="rooms-tab">{t('admin.rooms')}</TabsTrigger>
            <TabsTrigger value="blocked" data-testid="blocked-tab">
              <CalendarX className="mr-2 h-4 w-4" />
              {language === 'it' ? 'Blocco Date' : 'Blocked Dates'}
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              <KeyRound className="mr-2 h-4 w-4" />
              {language === 'it' ? 'Impostazioni' : 'Settings'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <Card className="p-6 border border-[#E3E5DF]/40">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>{t('booking.name')}</TableHead>
                    <TableHead>{t('booking.email')}</TableHead>
                    <TableHead>{t('admin.roomName')}</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead>{t('booking.total')}</TableHead>
                    <TableHead>{t('admin.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.booking_id} data-testid={`booking-row-${booking.booking_id}`}>
                      <TableCell className="font-mono text-xs">
                        {booking.booking_id.substring(0, 8)}
                      </TableCell>
                      <TableCell>{booking.guest_name}</TableCell>
                      <TableCell>{booking.guest_email}</TableCell>
                      <TableCell>{booking.room_name}</TableCell>
                      <TableCell>{booking.check_in}</TableCell>
                      <TableCell>{booking.check_out}</TableCell>
                      <TableCell>€{booking.total_price.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            booking.payment_status === 'paid' 
                              ? 'bg-[#8A9A86] hover:bg-[#8A9A86]' 
                              : 'bg-[#C1A58D] hover:bg-[#C1A58D]'
                          }
                        >
                          {booking.payment_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="rooms">
            <Card className="p-6 border border-[#E3E5DF]/40">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-light" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                  {t('admin.rooms')}
                </h2>
                <Dialog open={isRoomDialogOpen} onOpenChange={setIsRoomDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="add-room-button"
                      onClick={() => {
                        setEditingRoom(null);
                        resetRoomForm();
                      }}
                      className="bg-[#8A9A86] hover:bg-[#7A8A76] text-white"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {t('admin.addRoom')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>
                        {editingRoom ? t('admin.editRoom') : t('admin.addRoom')}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveRoom} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name_it">{t('admin.roomName')} (IT)</Label>
                          <Input
                            id="name_it"
                            value={roomForm.name_it}
                            onChange={(e) => setRoomForm({...roomForm, name_it: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="name_en">{t('admin.roomName')} (EN)</Label>
                          <Input
                            id="name_en"
                            value={roomForm.name_en}
                            onChange={(e) => setRoomForm({...roomForm, name_en: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="description_it">{t('admin.description')} (IT)</Label>
                        <Input
                          id="description_it"
                          value={roomForm.description_it}
                          onChange={(e) => setRoomForm({...roomForm, description_it: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="description_en">{t('admin.description')} (EN)</Label>
                        <Input
                          id="description_en"
                          value={roomForm.description_en}
                          onChange={(e) => setRoomForm({...roomForm, description_en: e.target.value})}
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="price">{t('admin.price')}</Label>
                          <Input
                            id="price"
                            type="number"
                            step="0.01"
                            value={roomForm.price}
                            onChange={(e) => setRoomForm({...roomForm, price: e.target.value})}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="max_guests">{t('admin.maxGuests')}</Label>
                          <Input
                            id="max_guests"
                            type="number"
                            value={roomForm.max_guests}
                            onChange={(e) => setRoomForm({...roomForm, max_guests: e.target.value})}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="image_url">{t('admin.imageUrl')}</Label>
                        <Input
                          id="image_url"
                          value={roomForm.image_url}
                          onChange={(e) => setRoomForm({...roomForm, image_url: e.target.value})}
                          required
                        />
                      </div>

                      <div className="flex gap-4 justify-end">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setIsRoomDialogOpen(false)}
                        >
                          {t('admin.cancel')}
                        </Button>
                        <Button 
                          type="submit" 
                          data-testid="save-room-button"
                          className="bg-[#8A9A86] hover:bg-[#7A8A76] text-white"
                        >
                          {t('admin.save')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.roomName')}</TableHead>
                    <TableHead>{t('admin.price')}</TableHead>
                    <TableHead>{t('admin.maxGuests')}</TableHead>
                    <TableHead>{t('admin.status')}</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rooms.map((room) => (
                    <TableRow key={room.room_id} data-testid={`room-row-${room.room_id}`}>
                      <TableCell>{room.name_it}</TableCell>
                      <TableCell>€{room.price}</TableCell>
                      <TableCell>{room.max_guests}</TableCell>
                      <TableCell>
                        <Badge className={room.is_active ? 'bg-[#8A9A86]' : 'bg-gray-400'}>
                          {room.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            data-testid={`edit-room-${room.room_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => openEditDialog(room)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-room-${room.room_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteRoom(room.room_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
          <TabsContent value="blocked">
            <Card className="p-6 border border-[#E3E5DF]/40 mb-6">
              <h2 className="text-2xl font-light mb-6" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {language === 'it' ? 'Blocca Date' : 'Block Dates'}
              </h2>
              <p className="text-sm text-[#2C302E]/60 mb-6">
                {language === 'it' 
                  ? 'Impedisci prenotazioni in date specifiche (ferie, manutenzione, ecc.)' 
                  : 'Prevent bookings on specific dates (holidays, maintenance, etc.)'}
              </p>

              <form onSubmit={handleCreateBlock} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                  <Label htmlFor="block-room">{language === 'it' ? 'Camera' : 'Room'}</Label>
                  <select
                    id="block-room"
                    data-testid="block-room-select"
                    value={blockForm.room_id}
                    onChange={(e) => setBlockForm({...blockForm, room_id: e.target.value})}
                    className="w-full mt-1 h-10 rounded-md border border-[#E3E5DF] px-3 text-sm bg-white"
                    required
                  >
                    <option value="">{language === 'it' ? 'Seleziona...' : 'Select...'}</option>
                    {rooms.filter(r => r.is_active).map(r => (
                      <option key={r.room_id} value={r.room_id}>{r.name_it}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="block-start">{language === 'it' ? 'Data Inizio' : 'Start Date'}</Label>
                  <Input
                    id="block-start"
                    type="date"
                    data-testid="block-start-date"
                    value={blockForm.start_date}
                    onChange={(e) => setBlockForm({...blockForm, start_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="block-end">{language === 'it' ? 'Data Fine' : 'End Date'}</Label>
                  <Input
                    id="block-end"
                    type="date"
                    data-testid="block-end-date"
                    value={blockForm.end_date}
                    onChange={(e) => setBlockForm({...blockForm, end_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="block-reason">{language === 'it' ? 'Motivo' : 'Reason'}</Label>
                  <Input
                    id="block-reason"
                    data-testid="block-reason-input"
                    placeholder={language === 'it' ? 'Es. Ferie' : 'E.g. Holidays'}
                    value={blockForm.reason}
                    onChange={(e) => setBlockForm({...blockForm, reason: e.target.value})}
                  />
                </div>
                <Button 
                  type="submit" 
                  data-testid="create-block-button"
                  disabled={creatingBlock}
                  className="bg-[#8A9A86] hover:bg-[#7A8A76] text-white"
                >
                  {creatingBlock ? t('common.loading') : (language === 'it' ? 'Blocca' : 'Block')}
                </Button>
              </form>
            </Card>

            <Card className="p-6 border border-[#E3E5DF]/40">
              <h3 className="text-xl font-light mb-4" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {language === 'it' ? 'Blocchi Attivi' : 'Active Blocks'}
              </h3>
              {blockedDates.length === 0 ? (
                <p className="text-[#2C302E]/60 text-sm">
                  {language === 'it' ? 'Nessun blocco attivo' : 'No active blocks'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{language === 'it' ? 'Camera' : 'Room'}</TableHead>
                      <TableHead>{language === 'it' ? 'Da' : 'From'}</TableHead>
                      <TableHead>{language === 'it' ? 'A' : 'To'}</TableHead>
                      <TableHead>{language === 'it' ? 'Motivo' : 'Reason'}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {blockedDates.map((block) => (
                      <TableRow key={block.block_id} data-testid={`block-row-${block.block_id}`}>
                        <TableCell>{block.room_name || block.room_id}</TableCell>
                        <TableCell>{block.start_date}</TableCell>
                        <TableCell>{block.end_date}</TableCell>
                        <TableCell>{block.reason || '-'}</TableCell>
                        <TableCell>
                          <Button
                            data-testid={`delete-block-${block.block_id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteBlock(block.block_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card className="p-8 border border-[#E3E5DF]/40 max-w-lg">
              <h2 className="text-2xl font-light mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                {language === 'it' ? 'Cambio Password' : 'Change Password'}
              </h2>
              <p className="text-sm text-[#2C302E]/60 mb-6">
                {language === 'it' 
                  ? 'Aggiorna la password del pannello admin' 
                  : 'Update the admin panel password'}
              </p>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="current-password">
                    {language === 'it' ? 'Password Attuale' : 'Current Password'}
                  </Label>
                  <Input
                    id="current-password"
                    type="password"
                    data-testid="current-password-input"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">
                    {language === 'it' ? 'Nuova Password (min 6 caratteri)' : 'New Password (min 6 chars)'}
                  </Label>
                  <Input
                    id="new-password"
                    type="password"
                    data-testid="new-password-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">
                    {language === 'it' ? 'Conferma Nuova Password' : 'Confirm New Password'}
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    data-testid="confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  data-testid="change-password-button"
                  disabled={changingPassword}
                  className="w-full bg-[#8A9A86] hover:bg-[#7A8A76] text-white"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  {changingPassword ? t('common.loading') : (language === 'it' ? 'Aggiorna Password' : 'Update Password')}
                </Button>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
